import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRouter } from './AppRouter';
import ToastProvider from './components/ui/ToastProvider';
// Create theme with the new professional enterprise banking light theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#0052CC',
      light: '#EFF6FF',
      dark: '#003DA8',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#F3F5F7',
      light: '#FFFFFF',
      dark: '#E5E7EB'
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF'
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280'
    },
    error: {
      main: '#AE2A19',
      light: '#FEF2F2'
    },
    success: {
      main: '#0B7923',
      light: '#ECFDF5'
    },
    warning: {
      main: '#B38600',
      light: '#FFFBEB'
    },
    info: {
      main: '#0052CC',
      light: '#EFF6FF'
    },
    divider: '#E5E7EB'
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: '32px',
      fontWeight: 700
    },
    h5: {
      fontSize: '20px',
      fontWeight: 700
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600
    },
    body1: {
      fontSize: '14px'
    },
    body2: {
      fontSize: '12px'
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 600
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 600
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          transition: 'none',
          scrollBehavior: 'smooth'
        },
        a: {
          color: 'inherit',
          textDecoration: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          transition: 'all 200ms ease',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.08)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        elevation1: {
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          borderRadius: 6,
          transition: 'all 200ms ease',
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #0052CC'
            }
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0052CC'
            }
          }
        },
        notchedOutline: {
          borderColor: '#D1D5DB',
          borderWidth: 1
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          color: '#374151'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '14px',
          padding: '10px 20px',
          transition: 'all 200ms ease',
          boxShadow: 'none'
        },
        contained: {
          backgroundColor: '#0052CC',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#003DA8',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.12)',
            transform: 'scale(1.02)'
          },
          '&.Mui-disabled': {
            backgroundColor: '#D1D5DB',
            color: '#9CA3AF'
          }
        },
        outlined: {
          borderColor: '#D1D5DB',
          borderWidth: 1,
          backgroundColor: '#F3F5F7',
          color: '#0052CC',
          '&:hover': {
            backgroundColor: '#E5E7EB',
            borderColor: '#D1D5DB',
            transform: 'scale(1.02)'
          }
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(0, 82, 204, 0.08)'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 600,
          fontSize: '12px',
          height: 20,
          transition: 'all 200ms ease'
        },
        filled: {
          backgroundColor: 'rgba(0, 82, 204, 0.1)',
          color: '#0052CC',
          border: 'none',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 82, 204, 0.2)'
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 82, 204, 0.15)'
          }
        },
        outlined: {
          border: '1px solid #D1D5DB',
          backgroundColor: 'transparent'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          padding: '12px 16px',
          borderColor: '#E5E7EB',
          color: '#374151'
        },
        head: {
          fontSize: '14px',
          fontWeight: 700,
          backgroundColor: '#F3F5F7',
          color: '#0052CC',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 200ms ease',
          '&:hover': {
            backgroundColor: '#F9FAFB'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 200ms ease',
          '&:hover': {
            backgroundColor: 'rgba(107, 114, 128, 0.08)'
          }
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'all 200ms ease',
          '&:hover': {
            backgroundColor: '#F9FAFB'
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E5E7EB',
          backgroundColor: '#F8F9FA'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid #E5E7EB'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1F2937',
          borderRadius: 6,
          padding: '6px 12px',
          fontSize: '12px',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.15)'
        },
        arrow: {
          color: '#1F2937'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0px 4px 20px rgba(0,0,0,0.1)'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          transition: 'all 200ms ease',
          fontSize: '14px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 82, 204, 0.08)',
            fontWeight: 600
          },
          '&:hover': {
            backgroundColor: '#F9FAFB'
          }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: 'rgba(0, 82, 204, 0.1)'
        },
        bar: {
          borderRadius: 4,
          backgroundColor: '#0052CC'
        }
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          strokeLinecap: 'round'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 46,
          height: 24,
          padding: 0
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(22px)',
            '& + .MuiSwitch-track': {
              backgroundColor: '#0052CC',
              opacity: 1
            }
          }
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        },
        track: {
          borderRadius: 50,
          opacity: 1,
          backgroundColor: '#D1D5DB'
        }
      }
    }
  }
});
// Scroll to top component to reset scroll position on route changes
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>('maker');
  // Check for existing session on app load
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const storedRole = localStorage.getItem('userRole');
    if (authStatus === 'true') {
      setIsLoggedIn(true);
      setUserRole(storedRole || 'maker');
    }
  }, []);
  const handleLogin = (username: string, password: string): boolean => {
    // Simple authentication check - supports admin (maker) and checker users
    // Admin password can be changed and persists via localStorage
    const adminPassword = localStorage.getItem('adminPassword') || 'test@1234';
    const checkerPassword = localStorage.getItem('checkerPassword') || 'test@1234';
    if (username === 'admin' && password === adminPassword || username === 'checker' && password === checkerPassword) {
      const role = username === 'checker' ? 'checker' : 'maker';
      // Pick a random bank name for this session
      const bankNames = ['Ecobank Ghana', 'Ecobank Nigeria', 'Ecobank Senegal', 'Ecobank Uganda', 'Ecobank Kenya', "Ecobank Côte d'Ivoire", 'Ecobank Togo', 'Stanbic Bank Ghana', 'Access Bank Nigeria', 'KCB Kenya', 'GTBank Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal', 'Paga Ghana'];
      const randomBank = bankNames[Math.floor(Math.random() * bankNames.length)];
      // Store authentication status and role in localStorage
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', role);
      localStorage.setItem('username', username);
      localStorage.setItem('bankName', randomBank);
      // Update React state to trigger re-render with correct role
      setUserRole(role);
      setIsLoggedIn(true);
      return true;
    }
    return false;
  };
  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserRole('maker');
    // Remove all authentication data from localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('bankName');
  };
  return <ThemeProvider theme={theme} data-id="element-0">
      <CssBaseline data-id="element-1" />
      <ToastProvider data-id="element-2">
        <BrowserRouter data-id="element-3">
          <ScrollToTop data-id="element-4" />
          <AppRouter isLoggedIn={isLoggedIn} onLogout={handleLogout} onLogin={handleLogin} data-id="element-5" />
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>;
}